import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, fromEvent } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { PaymentHistory } from 'src/app/api/models';
import { InvoicesService } from 'src/app/api/services';
import { PaginationDataSource } from 'src/app/model/pagination-data-source';

class IncomingPaymentQuery {
  invoice_id: number;
}

@Component({
  selector: 'app-incoming-payments-list-page',
  templateUrl: './incoming-payments-list-page.component.html',
  styleUrls: ['./incoming-payments-list-page.component.scss']
})
export class IncomingPaymentsListPageComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly ngDestroy$ = new Subject();

  @ViewChild('table', { read: ElementRef }) public matTableRef: ElementRef;

  q: IncomingPaymentQuery = new IncomingPaymentQuery();
  title = 'Incoming Payments';

  columns = [
    'created_at',
    'invoice_id',
    'amount',
    'is_payment',
    'client_name',
    'end_customer_name'
  ];

  dataSource = new PaginationDataSource<PaymentHistory, IncomingPaymentQuery>(
    (request, query) => this.invoicesService.listPaymentHistories({
      invoice_id: query.invoice_id,
      after: request.after,
      limit: request.limit
    }),
    { invoice_id: null },
    50,
  );

  constructor(
    private invoicesService: InvoicesService,
    private router: Router,
    private activatedRoute: ActivatedRoute
  ) {}

  ngOnInit() {
    this.activatedRoute.queryParamMap.pipe(takeUntil(this.ngDestroy$)).subscribe(params => {
      const invoiceId = params.get('invoice_id');
      this.queryBy({
        invoice_id: invoiceId ? parseInt(invoiceId, 10) : null
      });
    });
  }

  ngAfterViewInit(): void {
    fromEvent<Event>(this.matTableRef.nativeElement, 'scroll')
      .pipe(debounceTime(700))
      .subscribe((e) => this.onTableScroll(e));
  }

  updateQuery(params: Partial<IncomingPaymentQuery>) {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: params,
      queryParamsHandling: 'merge'
    });
  }

  queryBy(params: IncomingPaymentQuery) {
    this.q = params;
    this.dataSource.queryBy(this.q);
  }

  reset() {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: {}
    });
  }

  private onTableScroll(e: Event): void {
    // The table is the scroll's target: a scroll event does not bubble.
    const table = e.target;
    if (!(table instanceof HTMLElement)) {
      return;
    }
    const tableViewHeight = table.offsetHeight;
    const tableScrollHeight = table.scrollHeight;
    const scrollLocation = table.scrollTop;
    const scrollThreshold = 500;

    const scrollDownLimit = tableScrollHeight - tableViewHeight - scrollThreshold;
    if (scrollLocation > scrollDownLimit) {
      this.dataSource.fetchNextPage();
    }
  }

  ngOnDestroy() {
    this.ngDestroy$.next();
    this.ngDestroy$.complete();
  }
}
